import { DEV_USER_ID, DEV_USER_NAME } from '~/shared/config'

import type { Platform, PlatformUser, PlatformViewport, Unsubscribe } from '../types'

import { setBackButtonClickHandler, setBackButtonSnapshot } from './backButtonStore'
import { notifications } from '~/shared/lib/notifications'

const EMPTY_INSETS = { top: 0, right: 0, bottom: 0, left: 0 }

// Аватар-заглушка для фейкового пользователя в браузере — силуэт собачки на акцентном фоне.
const FALLBACK_AVATAR =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' +
      '<rect width="64" height="64" rx="32" fill="%23352e66"/>' +
      '<circle cx="32" cy="28" r="12" fill="%23ffd79b"/>' +
      '<path d="M14 54c2-11 9-17 18-17s16 6 18 17" fill="%23ffd79b"/>' +
      '</svg>',
  )

function copyToClipboard(value: string): void {
  navigator.clipboard
    ?.writeText(value)
    .then(() => notifications.success('Скопировано — вставь в чат вручную'))
    .catch(() => notifications.error('Не получилось скопировать'))
}

function readStartParam(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  return new URLSearchParams(window.location.search).get('startapp')
}

function readFakeUser(): PlatformUser | null {
  if (!DEV_USER_ID) {
    return null
  }
  return {
    id: Number(DEV_USER_ID),
    firstName: DEV_USER_NAME || 'Гостья',
    photoUrl: FALLBACK_AVATAR,
    languageCode: 'ru',
    // Вне Telegram бота нет, и разрешать ему нечего: считаем, что разрешение уже есть, —
    // тогда браузерная разработка не упирается в попап, которого всё равно не будет.
    allowsWriteToPm: true,
  }
}

/**
 * Адаптер для разработки в браузере (docs/telegram.md §1).
 * backButton пишет во внешний стор — его читает BrowserBackButtonRenderer.
 */
export function createBrowserPlatform(): Platform {
  let viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
      viewportHeight = window.innerHeight
    })
  }

  return {
    kind: 'BROWSER',
    initData: null,
    user: readFakeUser(),

    // Геттер — как в адаптере Telegram: адрес в браузере меняется без перезагрузки,
    // и снимок при монтировании разошёлся бы с тем, что видно в строке.
    get startParam(): string | null {
      return readStartParam()
    },

    get viewport(): PlatformViewport {
      return {
        // Нули, а не догадки: вне Telegram вырезы устройства знает только CSS —
        // ViewportRuntime подставит env(safe-area-inset-*).
        safeArea: EMPTY_INSETS,
        contentSafeArea: EMPTY_INSETS,
        height: viewportHeight,
        isExpanded: true,
      }
    },

    onViewportChanged(handler: () => void): Unsubscribe {
      window.addEventListener('resize', handler)
      window.addEventListener('orientationchange', handler)
      return () => {
        window.removeEventListener('resize', handler)
        window.removeEventListener('orientationchange', handler)
      }
    },

    backButton: {
      show() {
        setBackButtonSnapshot({ isVisible: true })
      },
      hide() {
        setBackButtonSnapshot({ isVisible: false })
      },
      onClick(handler: () => void): Unsubscribe {
        setBackButtonClickHandler(handler)
        return () => setBackButtonClickHandler(null)
      },
    },

    haptic: {
      impact(style) {
        console.debug('[platform:browser] haptic.impact', style)
      },
      notification(type) {
        console.debug('[platform:browser] haptic.notification', type)
      },
      selection() {
        console.debug('[platform:browser] haptic.selection')
      },
    },

    storage: {
      async get(key: string) {
        return window.localStorage.getItem(key)
      },
      async set(key: string, value: string) {
        window.localStorage.setItem(key, value)
      },
      async remove(key: string) {
        window.localStorage.removeItem(key)
      },
    },

    share: {
      openTelegramLink(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer')
      },
      openLink(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer')
      },
      shareToChat({ text, url }) {
        copyToClipboard(url ? `${text} ${url}` : text)
      },
      shareUrl({ text, url }) {
        // В браузере нет чатов Telegram — кладём в буфер ту же строку, что улетела бы в чат.
        copyToClipboard(`${text} ${url}`)
      },
      async sharePreparedMessage() {
        // Вне Telegram отправлять некуда — честное «не получилось», вызывающая сторона
        // уйдёт в фолбэк на ссылку и человек всё равно сможет её переслать.
        console.debug('[platform:browser] sharePreparedMessage — доступно только в Telegram')
        return { isSent: false, error: 'UNSUPPORTED' }
      },
      shareToStory(mediaUrl: string) {
        notifications.info('Шэринг в истории доступен только в Telegram')
        console.debug('[platform:browser] shareToStory', mediaUrl)
        return false
      },
    },

    requestWriteAccess() {
      console.debug('[platform:browser] requestWriteAccess() — недоступно вне Telegram')
      return Promise.resolve(false)
    },

    close() {
      console.debug('[platform:browser] close() — недоступно вне Telegram')
    },
    expand() {
      console.debug('[platform:browser] expand() — недоступно вне Telegram')
    },
    // Шапки Telegram в браузере нет — складываем цвета в переменные, чтобы их было видно
    // в девтулах и чтобы вызовы из PlatformProvider были одинаковыми на обеих платформах.
    setHeaderColor(color: string) {
      document.documentElement.style.setProperty('--tg-header-color', color)
    },
    setBackgroundColor(color: string) {
      document.documentElement.style.setProperty('--tg-background-color', color)
    },
    setBottomBarColor(color: string) {
      document.documentElement.style.setProperty('--tg-bottom-bar-color', color)
    },

    hideSettingsButton() {
      // Меню «…» есть только у Telegram.
    },

    setVerticalSwipesEnabled(isEnabled: boolean) {
      console.debug('[platform:browser] setVerticalSwipesEnabled', isEnabled)
    },
  }
}
